import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getCustomerMemberships, saveCustomerMembership } from "../../components/Api/GmsCustomerMembership";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import QuickAddCustomer from "../../components/QuickAddCustomer";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const getDefaultForm = () => {
  // Get user's branch ID from localStorage if available
  const userBranchId = localStorage.getItem("userBranchId");
  
  return {
    MembershipId: 0,
    CustomerId: null,
    BranchId: userBranchId ? Number(userBranchId) : null,
    Entity_Id: null,
    Entity_Type: null,
    PurchaseDate: new Date().toISOString().split('T')[0],
    CreatedBy: 1,
    ModifiedBy: 1,
  };
};

const emptyFilter = {
  MembershipId: null,
  CustomerId: null,
  BranchId: null,
  Entity_Type: null,
  From_Date: null,
  To_Date: null,
};

export default function MembershipPage() {
  const [memberships, setMemberships] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [spaPackages, setSpaPackages] = useState([]);
  const [spaPackageSeries, setSpaPackageSeries] = useState([]);
  const [spaServices, setSpaServices] = useState([]);
  const [form, setForm] = useState(getDefaultForm());
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Check if user has a specific branch assigned
  const userBranchId = localStorage.getItem("userBranchId");
  const isBranchReadOnly = !!userBranchId;
  
  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsMembership');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsMembership');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsMembership');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsMembership');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadCustomers();
      loadBranches();
      loadSpaPackages();
      loadSpaPackageSeries();
      loadSpaServices();
      loadMemberships();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadMemberships();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ customer_Active: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    console.log('🔍 searchCustomersManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      const options = data.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranchOptions(options);
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadSpaPackages = async () => {
    try {
      const data = await getSpaPackages({ Status: true });
      setSpaPackages(data || []);
    } catch (error) {
      console.error("Error loading spa packages:", error);
      Swal.fire("Error", "Failed to load spa packages", "error");
    }
  };

  const loadSpaPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ Status: true });
      setSpaPackageSeries(data || []);
    } catch (error) {
      console.error("Error loading spa package series:", error);
      Swal.fire("Error", "Failed to load spa package series", "error");
    }
  };

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({ Status: true });
      setSpaServices(data || []);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire("Error", "Failed to load spa services", "error");
    }
  };

  const loadMemberships = async () => {
    try {
      setLoading(true);
      const data = await getCustomerMemberships(filter);
      
      // Transform API response to match frontend expectations
      const transformedData = (data || []).map(membership => ({
        MembershipId: membership.membershipId,
        CustomerId: membership.customerId,
        BranchId: membership.branchId,
        Entity_Id: membership.entity_Id,
        Entity_Type: membership.entity_Type,
        PurchaseDate: membership.purchaseDate,
        CreatedOn: membership.createdOn,
        CreatedBy: membership.createdBy,
        ModifiedOn: membership.modifiedOn,
        ModifiedBy: membership.modifiedBy,
        details: membership.details || [],
        installments: (membership.installments || []).map(installment => ({
          installmentId: installment.installmentId,
          installmentName: installment.installmentName,
          amount: installment.installmentAmount || installment.amount,
          paidAmount: installment.paidAmount || 0
        }))
      }));
      
      setMemberships(transformedData);
      setTotalCount(transformedData.length);
    } catch (error) {
      console.error("Error loading memberships:", error);
      Swal.fire("Error", "Failed to load memberships", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!form.CustomerId) {
        Swal.fire("Error", "Please select a customer", "error");
        return;
      }
      if (!form.BranchId) {
        Swal.fire("Error", "Please select a branch", "error");
        return;
      }
      if (!form.Entity_Type) {
        Swal.fire("Error", "Please select entity type", "error");
        return;
      }
      if (!form.Entity_Id) {
        Swal.fire("Error", "Please select a package or package series", "error");
        return;
      }
      if (!form.PurchaseDate) {
        Swal.fire("Error", "Please select purchase date", "error");
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();
      
      const saveData = {
        ...form,
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: session.userId,
        CreatedOn: isEditing ? form.CreatedOn : new Date().toISOString(),
        ModifiedOn: new Date().toISOString(),
      };

      const result = await saveCustomerMembership(saveData);
      Swal.fire("Success", "Membership saved successfully", "success");
      handleClear();
      loadMemberships();
      setActiveTab("2");
    } catch (error) {
      console.error("Error saving membership:", error);
      const errorMessage = error.response?.data?.message || "Failed to save membership";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (membership) => {
    try {
      setEditLoadingRow(membership.MembershipId);
      
      setForm({
        MembershipId: membership.MembershipId,
        CustomerId: membership.CustomerId,
        BranchId: membership.BranchId,
        Entity_Id: membership.Entity_Id,
        Entity_Type: membership.Entity_Type,
        PurchaseDate: membership.PurchaseDate ? membership.PurchaseDate.split('T')[0] : '',
        CreatedBy: membership.CreatedBy,
        CreatedOn: membership.CreatedOn,
        ModifiedBy: membership.ModifiedBy,
        ModifiedOn: membership.ModifiedOn,
      });

      // Set the selected customer option for display
      const customer = customers.find(c => c.customer_Id === membership.CustomerId);
      if (customer) {
        setSelectedCustomerOption({
          value: customer.customer_Id,
          label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`
        });
      }

      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      console.error("Error loading membership for edit:", error);
      Swal.fire("Error", "Failed to load membership for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (membership) => {
    try {
      const customer = customers.find(c => c.customer_Id === membership.CustomerId);
      const branch = branches.find(b => b.branch_Id === membership.BranchId);
      const entityName = membership.Entity_Type === 'pac' 
        ? spaPackages.find(p => p.packageId === membership.Entity_Id)?.packageName_En
        : spaPackageSeries.find(s => s.seriesId === membership.Entity_Id)?.seriesName_En;

      // Generate grouped package and service details HTML
      const packageServiceDetailsHtml = membership.details && membership.details.length > 0 
        ? (() => {
            // Group details by package
            const groupedByPackage = membership.details.reduce((acc, detail) => {
              const packageName = getPackageName(detail.packageId);
              if (!acc[packageName]) {
                acc[packageName] = {
                  packageId: detail.packageId,
                  services: []
                };
              }
              acc[packageName].services.push({
                serviceId: detail.serviceId,
                serviceName: getServiceName(detail.serviceId),
                completionStatus: detail.completionStatus
              });
              return acc;
            }, {});

            // Generate HTML for grouped packages
            return Object.entries(groupedByPackage).map(([packageName, packageData]) => `
              <div style="background: #fff; padding: 10px; border-radius: 6px; border: 1px solid #e9ecef; margin-bottom: 8px;">
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${packageName}</span>
                </div>
                <div>
                  <strong style="color: #495057;">Services:</strong>
                  <div style="margin-top: 5px;">
                    ${packageData.services.map(service => `
                      <div style="margin-bottom: 3px;">
                        <span style="color: #6c757d;">${service.serviceName}</span>
                      </div>
                    `).join('')}
                  </div>
                </div>
              </div>
            `).join('');
          })()
        : '<div style="text-align: center; color: #6c757d; padding: 20px;">No package and service details available</div>';

      // Fetch package series data to get installment names
      let installmentNames = {};
      if (membership.installments && membership.installments.length > 0 && membership.Entity_Type === 'sep') {
        try {
          const seriesData = await getSpaPackageSeries({ seriesId: membership.Entity_Id });
          if (seriesData && seriesData.length > 0 && seriesData[0].installments) {
            seriesData[0].installments.forEach(installment => {
              installmentNames[installment.installmentId] = installment.installmentName;
            });
          }
        } catch (error) {
          console.error('Error fetching package series data:', error);
        }
      }

      // Calculate total balance (total amount - total billed amount)
      const totalAmount = membership.installments ? membership.installments.reduce((sum, installment) => sum + (installment.amount || 0), 0) : 0;
      const totalBilledAmount = membership.installments ? membership.installments.reduce((sum, installment) => sum + (installment.billedAmount || 0), 0) : 0;
      const totalBalance = totalAmount - totalBilledAmount;

      // Generate installments HTML with proper names
      const installmentsHtml = membership.installments && membership.installments.length > 0
        ? membership.installments.map(installment => {
            const installmentName = installmentNames[installment.installmentId] || `Installment ${installment.installmentId}`;
            return `
            <div style="background: #fff; padding: 10px; border-radius: 6px; border: 1px solid #e9ecef; margin-bottom: 8px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <strong style="color: #495057;">${installmentName}:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(installment.amount || 0).toFixed(2)}</span>
                </div>
                <div>
                  <span style="color: #6c757d;">Billed: ${(installment.billedAmount || 0).toFixed(2)}</span>
                  <span class="badge ${(installment.billedAmount || 0) >= (installment.amount || 0) ? 'bg-success' : 'bg-warning'} ms-2">
                    ${(installment.billedAmount || 0) >= (installment.amount || 0) ? 'Billed' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          `;
          }).join('')
        : '<div style="text-align: center; color: #6c757d; padding: 20px;">No installment details available</div>';

      Swal.fire({
        title: `<div style="text-align: center; color: #556ee6; font-size: 24px; font-weight: bold; margin-bottom: 10px;">
                  <i class="bx bx-id-card" style="margin-right: 10px;"></i>
                  Membership Details
                </div>`,
        html: `
          <div style="max-width: 900px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #556ee6;">
                <h6 style="color: #556ee6; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                  Basic Information
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Customer:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${customer?.customer_NameEn || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Branch:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${branch?.branch_NameEn || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Entity Type:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${membership.Entity_Type === 'pac' ? 'Package' : 'Package Series'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Purchase Date:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${membership.PurchaseDate ? new Date(membership.PurchaseDate).toLocaleDateString('en-GB') : 'N/A'}</span>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
                <h6 style="color: #28a745; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-package" style="margin-right: 5px;"></i>
                  Membership Details
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Entity Name:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${entityName || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Amount:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${totalAmount.toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Remaining Balance:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${totalBalance.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
                <h6 style="color: #ffc107; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-spa" style="margin-right: 5px;"></i>
                  Package & Service Details
                </h6>
                <div style="max-height: 200px; overflow-y: auto;">
                  ${packageServiceDetailsHtml}
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #17a2b8;">
                <h6 style="color: #17a2b8; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-credit-card" style="margin-right: 5px;"></i>
                  Installments
                </h6>
                <div style="max-height: 200px; overflow-y: auto;">
                  ${installmentsHtml}
                </div>
              </div>
            </div>
          </div>
        `,
        confirmButtonText: "Close",
        width: '1000px',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          confirmButton: 'btn btn-primary',
          popup: 'swal2-custom-popup'
        }
      });
    } catch (error) {
      console.error("Error viewing membership:", error);
      Swal.fire("Error", "Failed to view membership details", "error");
    }
  };

  const handleClear = () => {
    setForm(getDefaultForm());
    setSelectedCustomerOption(null);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    setHasAppliedFilter(true);
  };

  const handleCustomerAdded = async (newCustomer) => {
    console.log("🔍 MembershipPage - handleCustomerAdded received:", newCustomer);
    
    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;
    console.log("🔍 Looking for customer ID:", customerId);
    
    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });
      console.log("🔍 Fetched customer data:", customerData);
      
      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        console.log("🔍 Found customer:", addedCustomer);
        
        // Set the newly added customer as selected
        setForm({ ...form, CustomerId: customerId });
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        console.log("🔍 Set customer option:", `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`);
        
        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm({ ...form, CustomerId: customerId });
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        console.log("🔍 Customer not found, using fallback");
        
        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm({ ...form, CustomerId: customerId });
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
      console.log("🔍 Error occurred, using fallback");
    }
  };


  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = memberships.slice(startIndex, endIndex);

  // Options for dropdowns
  const customerOptions = customers.map(customer => ({
    value: customer.customer_Id,
    label: customer.customer_NameEn
  }));


  const packageOptions = spaPackages.map(pkg => ({
    value: pkg.packageId,
    label: pkg.packageName_En
  }));

  const packageSeriesOptions = spaPackageSeries.map(series => ({
    value: series.seriesId,
    label: series.seriesName_En
  }));

  const entityTypeOptions = [
    { value: 'pac', label: 'Package' },
    { value: 'sep', label: 'Package Series' }
  ];

  const entityTypeFilterOptions = [
    { value: 'pac', label: 'Package' },
    { value: 'sep', label: 'Package Series' }
  ];

  // Get names by ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? customer.customer_NameEn : "N/A";
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : "N/A";
  };

  const getEntityName = (entityId, entityType) => {
    if (entityType === 'pac') {
      const pkg = spaPackages.find(p => p.packageId === entityId);
      return pkg ? pkg.packageName_En : "N/A";
    } else {
      const series = spaPackageSeries.find(s => s.seriesId === entityId);
      return series ? series.seriesName_En : "N/A";
    }
  };

  const getServiceName = (serviceId) => {
    const service = spaServices.find(s => s.spaService_Id === serviceId);
    return service ? service.spaServiceName_En : "N/A";
  };

  const getPackageName = (packageId) => {
    const pkg = spaPackages.find(p => p.packageId === packageId);
    return pkg ? pkg.packageName_En : "N/A";
  };

  const getEntityAmount = (entityId, entityType) => {
    if (entityType === 'pac') {
      const pkg = spaPackages.find(p => p.packageId === entityId);
      return pkg ? pkg.amount : 0;
    } else {
      const series = spaPackageSeries.find(s => s.seriesId === entityId);
      return series ? series.amount : 0;
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Membership Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Membership</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Membership List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="CustomerId">Customer <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    value={selectedCustomerOption}
                                    onChange={(selected) => {
                                      setSelectedCustomerOption(selected);
                                      setForm({ ...form, CustomerId: selected?.value || null });
                                    }}
                                    options={customerSearchOptions}
                                    placeholder="Type at least 3 characters to search customers..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    maxMenuHeight={200}
                                    isLoading={customerSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters to search"}
                                    loadingMessage={() => "Searching customers..."}
                                    onInputChange={(inputValue) => {
                                      console.log('🔤 Input changed:', inputValue);
                                      
                                      // Clear previous timeout
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }
                                      
                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        searchCustomersManually(inputValue);
                                      }, 300);
                                      setSearchTimeout(timeout);
                                      
                                      return inputValue;
                                    }}
                                    onMenuOpen={() => console.log('📂 Menu opened')}
                                    onMenuClose={() => console.log('📁 Menu closed')}
                                  />
                                </div>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => setShowQuickAddCustomer(true)}
                                  disabled={isEditing}
                                  title="Quick Add Customer"
                                >
                                  <i className="fas fa-plus"></i>
                                </Button>
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="BranchId">Branch <span className="text-danger">*</span></Label>
                              <Select
                                value={branchOptions.find(option => option.value === form.BranchId) || null}
                                onChange={(selected) => setForm({ ...form, BranchId: selected?.value || null })}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable={!isBranchReadOnly}
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!!localStorage.getItem("userBranchId")}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Entity_Type">Entity Type <span className="text-danger">*</span></Label>
                              <Select
                                value={entityTypeOptions.find(option => option.value === form.Entity_Type)}
                                onChange={(selected) => {
                                  setForm({ 
                                    ...form, 
                                    Entity_Type: selected?.value || null,
                                    Entity_Id: null // Reset entity selection when type changes
                                  });
                                }}
                                options={entityTypeOptions}
                                placeholder="Select Entity Type"
                                styles={selectStyles}
                                isClearable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Entity_Id">
                                {form.Entity_Type === 'pac' ? 'Package' : form.Entity_Type === 'sep' ? 'Package Series' : 'Entity'} 
                                <span className="text-danger">*</span>
                              </Label>
                              <Select
                                value={form.Entity_Type === 'pac' 
                                  ? packageOptions.find(option => option.value === form.Entity_Id)
                                  : packageSeriesOptions.find(option => option.value === form.Entity_Id)
                                }
                                onChange={(selected) => setForm({ ...form, Entity_Id: selected?.value || null })}
                                options={form.Entity_Type === 'pac' ? packageOptions : packageSeriesOptions}
                                placeholder={`Select ${form.Entity_Type === 'pac' ? 'Package' : form.Entity_Type === 'sep' ? 'Package Series' : 'Entity'}`}
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!form.Entity_Type}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PurchaseDate">Purchase Date <span className="text-danger">*</span></Label>
                              <Input
                                id="PurchaseDate"
                                name="PurchaseDate"
                                type="date"
                                value={form.PurchaseDate}
                                onChange={(e) => setForm({ ...form, PurchaseDate: e.target.value })}
                                required
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.CustomerId || !form.BranchId || !form.Entity_Type || !form.Entity_Id || !form.PurchaseDate}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Membership" : "Save Membership"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Memberships</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label for="filterCustomerId">Customer</Label>
                                <Select
                                  value={selectedCustomerFilterOption}
                                  onChange={(selected) => {
                                    setSelectedCustomerFilterOption(selected);
                                    setFilter({ ...filter, CustomerId: selected?.value || null });
                                  }}
                                  options={customerSearchOptions}
                                  placeholder="Type at least 3 characters to search customers..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={customerSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching customers..."}
                                  onInputChange={(inputValue) => {
                                    console.log('🔤 Input changed:', inputValue);
                                    
                                    // Clear previous timeout
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }
                                    
                                    // Set new timeout
                                    const timeout = setTimeout(() => {
                                      searchCustomersManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);
                                    
                                    return inputValue;
                                  }}
                                  onMenuOpen={() => console.log('📂 Menu opened')}
                                  onMenuClose={() => console.log('📁 Menu closed')}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterBranchId">Branch</Label>
                                <Select
                                  value={branchOptions.find(option => option.value === filter.BranchId) || null}
                                  onChange={(selected) => setFilter({ ...filter, BranchId: selected?.value || null })}
                                  options={branchOptions}
                                  placeholder="Select Branch"
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterEntityType">Entity Type</Label>
                                <Select
                                  value={entityTypeFilterOptions.find(option => option.value === filter.Entity_Type)}
                                  onChange={(selected) => setFilter({ ...filter, Entity_Type: selected?.value || null })}
                                  options={entityTypeFilterOptions}
                                  placeholder="Select Entity Type"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterFromDate">From Date</Label>
                                <Input
                                  id="filterFromDate"
                                  name="filterFromDate"
                                  type="date"
                                  value={filter.From_Date ? filter.From_Date.split('T')[0] : ''}
                                  onChange={(e) => setFilter({ ...filter, From_Date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterToDate">To Date</Label>
                                <Input
                                  id="filterToDate"
                                  name="filterToDate"
                                  type="date"
                                  value={filter.To_Date ? filter.To_Date.split('T')[0] : ''}
                                  onChange={(e) => setFilter({ ...filter, To_Date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Membership List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => setActiveTab("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Membership
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading memberships...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="15%">Branch Name</th>
                                      <th width="20%">Customer Name</th>
                                      <th width="15%">Entity Type</th>
                                      <th width="25%">Entity Name</th>
                                      <th width="15%">Purchase Date</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.MembershipId}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{getBranchName(item.BranchId)}</td>
                                          <td>{getCustomerName(item.CustomerId)}</td>
                                          <td>
                                            <span className={`badge ${item.Entity_Type === 'pac' ? 'bg-primary' : 'bg-info'}`}>
                                              {item.Entity_Type === 'pac' ? 'Package' : 'Package Series'}
                                            </span>
                                          </td>
                                          <td>{getEntityName(item.Entity_Id, item.Entity_Type)}</td>
                                          <td>{item.PurchaseDate ? new Date(item.PurchaseDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.MembershipId}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.MembershipId ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="text-center">
                                          {hasAppliedFilter ? "No memberships found matching the filter criteria." : "No memberships available. Please add some memberships first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />
    </div>
  );
}
